import { ChevronLeft, ChevronRight } from 'lucide-react'
import type * as React from 'react'
import { DayPicker } from 'react-day-picker'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const calendarClassNames = {
  months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-6 sm:space-y-0',
  month: 'space-y-4',
  month_caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-sm font-medium',
  nav: 'space-x-1 flex items-center',
  button_previous: cn(
    buttonVariants({ variant: 'outline' }),
    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-4'
  ),
  button_next: cn(
    buttonVariants({ variant: 'outline' }),
    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-2'
  ),
  month_grid: 'w-full border-collapse space-y-1',
  weekdays: 'flex',
  weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
  week: 'flex w-full mt-2',
  day: cn(
    'h-9 w-9 text-center text-sm p-0 relative rounded-none',
    '[&:has([aria-selected])]:bg-accent',
    '[&:has([aria-selected].day-outside)]:bg-accent/50',
    '[&:has([aria-selected].day-range-start):not(:has(.day-range-end))]:rounded-l-3xl [&:has([aria-selected].day-range-start):not(:has(.day-range-end))]:rounded-r-none',
    '[&:has([aria-selected].day-range-end):not(:has(.day-range-start))]:rounded-r-3xl [&:has([aria-selected].day-range-end):not(:has(.day-range-start))]:rounded-l-none',
    '[&:has([aria-selected].day-range-start.day-range-end)]:rounded-3xl',
    'focus-within:relative focus-within:z-20'
  ),
  day_button: cn(
    buttonVariants({ variant: 'ghost' }),
    'h-9 w-9 p-0 font-normal aria-selected:opacity-100'
  ),
  range_start: 'day-range-start rounded-l-3xl rounded-r-none',
  range_end: 'day-range-end rounded-r-3xl rounded-l-none',
  selected: cn(
    'bg-primary text-primary-foreground',
    'focus:bg-primary focus:text-primary-foreground',
    '[&.day-range-start]:rounded-l-3xl [&.day-range-start]:rounded-r-none',
    '[&.day-range-end]:rounded-r-3xl [&.day-range-end]:rounded-l-none',
    '[&.day-range-start]:hover:bg-accent [&.day-range-start]:hover:text-accent-foreground',
    '[&.day-range-end]:hover:bg-accent [&.day-range-end]:hover:text-accent-foreground'
  ),
  today: 'bg-accent text-accent-foreground',
  outside: cn(
    'day-outside text-muted-foreground',
    'aria-selected:bg-accent/50 aria-selected:text-muted-foreground'
  ),
  disabled: 'text-muted-foreground opacity-50',
  range_middle:
    'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none',
  hidden: 'invisible',
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('relative pl-10 pr-14 py-4', className)}
      classNames={{
        ...calendarClassNames,
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4" />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar, calendarClassNames }
